import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Center,
  Heading,
  Spinner,
  Button,
  Divider,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { formatNameLastCommaFirst } from '../utils/clientNameDisplay';
import { formatMakeupBadgeDate } from '../utils/attendanceDisplay';
import {
  SLOT_SOURCE_LABELS,
  SLOT_SOURCES,
} from '../utils/makeupOpportunityUtils';

const SLOT_SOURCE_COLORS = {
  [SLOT_SOURCES.OPEN]: 'green',
  [SLOT_SOURCES.CANCELLED]: 'orange',
  [SLOT_SOURCES.LUNCH]: 'yellow',
};

const GAP_LABELS = {
  before: 'Before session',
  after: 'After session',
  between: 'Between sessions',
};

const MakeupOpportunitiesView = ({
  opportunities = [],
  loading = false,
  targetDate,
  formatTime,
  onScheduleOpportunity,
}) => {
  const formattedDate = targetDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const totalMatches = opportunities.reduce((sum, item) => sum + item.matches.length, 0);

  if (loading) {
    return (
      <Center py={12}>
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <Heading size="md">Makeup Opportunities</Heading>
        <Badge colorScheme={totalMatches > 0 ? 'green' : 'gray'} fontSize="md" px={3} py={1}>
          {totalMatches} match{totalMatches === 1 ? '' : 'es'}
        </Badge>
      </HStack>

      <Text fontSize="sm" color="gray.600">
        {formattedDate} — open therapist slots matched with clients who have pending makeups and
        same-day availability before or after their session. Therapists must have at least one
        active client scheduled that day.
      </Text>

      <HStack spacing={3} flexWrap="wrap">
        {Object.entries(SLOT_SOURCE_LABELS).map(([source, label]) => (
          <HStack key={source} spacing={1}>
            <Badge colorScheme={SLOT_SOURCE_COLORS[source]} fontSize="xs">
              {label}
            </Badge>
          </HStack>
        ))}
        <HStack spacing={1}>
          <Badge colorScheme="purple" fontSize="xs" variant="solid">
            Same therapist
          </Badge>
          <Text fontSize="xs" color="gray.600">
            Original therapist match — listed first
          </Text>
        </HStack>
      </HStack>

      {opportunities.length === 0 ? (
        <Center py={10}>
          <Text color="gray.500" textAlign="center" maxW="520px">
            No makeup opportunities found for this date. Opportunities appear when a therapist has
            an open slot, a cancelled session slot, or a lunch break, and a client with a pending
            makeup is scheduled before or after that time.
          </Text>
        </Center>
      ) : (
        opportunities.map(({ slot, matches }) => (
          <Box key={`${slot.therapistId}_${slot.time}`} borderWidth={1} borderRadius="md" overflow="hidden" bg="white">
            <Box px={4} py={3} bg="green.50" borderBottomWidth={1}>
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <HStack flexWrap="wrap">
                  <Text fontWeight="semibold">{formatTime(slot.time)}</Text>
                  <Text color="gray.700">{slot.therapist.name}</Text>
                  <Badge colorScheme="pink">{slot.therapist.specialization}</Badge>
                  <Badge colorScheme={SLOT_SOURCE_COLORS[slot.source]}>
                    {SLOT_SOURCE_LABELS[slot.source]}
                  </Badge>
                </HStack>
                <Badge colorScheme="green">{matches.length} client{matches.length === 1 ? '' : 's'}</Badge>
              </HStack>
            </Box>

            <VStack align="stretch" spacing={0} divider={<Divider />}>
              {matches.map((match) => {
                const client = match.client;
                const pending = match.pendingMakeup;

                return (
                  <HStack
                    key={`${slot.therapistId}_${slot.time}_${pending.id}`}
                    px={4}
                    py={3}
                    justify="space-between"
                    align="start"
                    flexWrap="wrap"
                    gap={3}
                    bg={match.sameOriginalTherapist ? 'purple.50' : 'white'}
                    borderLeftWidth={match.sameOriginalTherapist ? 4 : 0}
                    borderLeftColor={match.sameOriginalTherapist ? 'purple.400' : 'transparent'}
                  >
                    <Box flex={1} minW="220px">
                      <HStack spacing={2} flexWrap="wrap" align="center">
                        <Text fontSize="sm" fontWeight={match.sameOriginalTherapist ? 'bold' : 'medium'}>
                          {formatNameLastCommaFirst(client?.name)}
                        </Text>
                        {match.sameOriginalTherapist && (
                          <Badge colorScheme="purple" fontSize="xs">
                            Same therapist
                          </Badge>
                        )}
                      </HStack>
                      {client?.nickname && (
                        <Text fontSize="xs" color="gray.500">
                          {client.nickname}
                        </Text>
                      )}
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        Pending makeup from {formatMakeupBadgeDate(pending.attendance_date)}
                        {pending.therapist?.name ? ` · originally ${pending.therapist.name}` : ''}
                      </Text>
                      <HStack mt={2} spacing={2} flexWrap="wrap">
                        <Badge colorScheme="purple" fontSize="xs">
                          {GAP_LABELS[match.gap] || match.gap}
                        </Badge>
                        {match.adjacentHour != null && (
                          <Badge colorScheme="gray" fontSize="xs">
                            Near {formatTime(`${match.adjacentHour}:00`)}
                          </Badge>
                        )}
                      </HStack>
                    </Box>

                    {onScheduleOpportunity && (
                      <Button
                        size="sm"
                        colorScheme="blue"
                        leftIcon={<AddIcon />}
                        onClick={() =>
                          onScheduleOpportunity({
                            slot,
                            match,
                            pendingMakeupId: pending.id,
                            clientId: client?.id,
                            therapistId: slot.therapistId,
                            time: slot.time,
                          })
                        }
                      >
                        Schedule
                      </Button>
                    )}
                  </HStack>
                );
              })}
            </VStack>
          </Box>
        ))
      )}
    </VStack>
  );
};

export default MakeupOpportunitiesView;
